/**
 * @fileoverview Checkout payload validation.
 * Supports both single item (current API) and items array (future API).
 */

import type {
  ValidationResult,
  CheckoutValidationInput,
  CheckoutItemValidationInput,
} from "./types";

/**
 * Validate a single checkout item.
 *
 * @param item - The item to validate
 * @param label - Label for error messages (e.g., "Item" or "Item at index 0")
 */
function validateItem(
  item: CheckoutItemValidationInput,
  label: string,
): string[] {
  const errors: string[] = [];

  if (typeof item !== "object" || item === null) {
    errors.push(`${label} must be an object`);
    return errors;
  }

  if (typeof item.type !== "string" || item.type.trim() === "") {
    errors.push(`${label} must have a valid type`);
  }

  if (typeof item.slug !== "string" || item.slug.trim() === "") {
    errors.push(`${label} must have a valid slug`);
  }

  if (
    item.quantity !== undefined &&
    (typeof item.quantity !== "number" || item.quantity < 1)
  ) {
    errors.push(`${label} must have a quantity >= 1`);
  }

  return errors;
}

const CHECKOUT_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const CHECKOUT_ID_MAX_LENGTH = 64;

/**
 * Message returned when `checkout_id` is combined with the item/items form.
 * Shared across the validator and the checkout module so the mixing rule
 * reads the same everywhere.
 */
export const CHECKOUT_ID_MIXED_MESSAGE =
  "checkout_id cannot be combined with item or items";

/**
 * Validate a server-created checkout ID.
 */
function validateCheckoutId(checkoutId: unknown): string[] {
  if (typeof checkoutId !== "string" || checkoutId === "") {
    return ["checkout_id must be a non-empty string"];
  }
  if (checkoutId.length > CHECKOUT_ID_MAX_LENGTH) {
    return [`checkout_id must be at most ${CHECKOUT_ID_MAX_LENGTH} characters`];
  }
  if (!CHECKOUT_ID_PATTERN.test(checkoutId)) {
    return ["checkout_id may only contain letters, numbers, '_' and '-'"];
  }
  return [];
}

/**
 * Validate checkout options.
 * Supports both single item format (current) and items array (future).
 *
 * Single item format: { item: { type, slug, quantity? }, context? }
 * Array format: { items: [{ type, slug, quantity? }], context? }
 */
export function validateCheckout(
  payload: CheckoutValidationInput,
): ValidationResult {
  const errors: string[] = [];

  if (typeof payload !== "object" || payload === null) {
    errors.push("Checkout options must be an object");
    return { valid: false, errors };
  }

  // Support single item (item), array (items), and checkout_id formats.
  const hasSingleItem = "item" in payload;
  const hasItemsArray = "items" in payload;

  if ("checkout_id" in payload) {
    // A checkout_id payload must not be mixed with item/items. (Flat
    // CheckoutItem fields — type/slug/quantity — are caught by the module.)
    if (hasSingleItem || hasItemsArray) {
      errors.push(CHECKOUT_ID_MIXED_MESSAGE);
      return { valid: false, errors };
    }
    errors.push(...validateCheckoutId(payload.checkout_id));
    return { valid: errors.length === 0, errors };
  }

  if (!hasSingleItem && !hasItemsArray) {
    errors.push("Checkout requires either 'item' or 'items'");
    return { valid: false, errors };
  }

  // Single item format (current API)
  if (hasSingleItem) {
    const itemErrors = validateItem(
      payload.item as CheckoutItemValidationInput,
      "Item",
    );
    errors.push(...itemErrors);
    return { valid: errors.length === 0, errors };
  }

  // Array format (future API)
  if (!Array.isArray(payload.items)) {
    errors.push("Checkout items must be an array");
    return { valid: false, errors };
  }

  if (payload.items.length === 0) {
    errors.push("At least one item is required");
    return { valid: false, errors };
  }

  // Validate each item
  payload.items.forEach((item, index) => {
    const itemErrors = validateItem(
      item as CheckoutItemValidationInput,
      `Item at index ${index}`,
    );
    errors.push(...itemErrors);
  });

  return { valid: errors.length === 0, errors };
}
